<template>
  <div class="card card-commissions-closed-students">
    <div class="card-body">
      <div v-if="isLoadingCommissions" class="loading-spinner text-center">
        <div class="spinner-border" role="status" />
        <small>Loading data..</small>
      </div>
      <template v-else>
        <div v-if="errorMessage" class="text-center py-4">{{ errorMessage }}</div>
        <div v-else :class="{'table-responsive': students.length > 0}">
          <table class="table closed-students-table mb-0">
            <thead>
              <tr class="bg-white">
                <th scope="col" class="text-center">No</th>
                <th scope="col">Student</th>
                <th scope="col" class="text-center">Type</th>
                <th scope="col">Course</th>
                <th scope="col">Tuition</th>
                <th scope="col">Comm</th>
                <th scope="col" class="text-nowrap">Qty</th>
                <th scope="col" class="text-nowrap">KeyP/Level</th>
                <th scope="col">Ref/Hunter/Self</th>
                <th scope="col" class="text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(group, groupIndex) in groupedStudents">
                <tr v-if="group.students.length > 0" :key="`${groupIndex}-${group.students[0].student.id}`" :class="getTypeClass(group.students[0].type, group.students[0].status)">
                  <td :rowspan="group.count" class="text-center">{{ groupIndex + 1 }}</td>
                  <td class="student-name" :title="group.students[0].student.full_name" :rowspan="group.count" >
                    <a :href="group.students[0].student.url" target="_blank">{{ truncatedName(group.students[0].student.full_name) }}</a>
                  </td>
                  <td class="text-center">{{ getStudentType(group.students[0].type, group.students[0].status) }}</td>
                  <td :title="group.students[0].course">{{ truncatedName(group.students[0].course) }}</td>
                  <td>{{ group.students[0].tuition_fee ? formattedNumbers(group.students[0].tuition_fee) : '-' }}</td>
                  <td>{{ formattedNumbers(group.students[0].commission) }}</td>
                  <td>{{ getQtyPercentage(group.students[0].qty_percentage) }}</td>
                  <td>{{ getEnhancerPercentage(group.students[0].enhancers, ['level', 'key_partner']) }}</td>
                  <td>{{ getEnhancerPercentage(group.students[0].enhancers, ['referer', 'hunter', 'self']) }}</td>
                  <td class="commission-subtotal text-right">
                    <commission-transactions-tooltip :transactions="group.students[0].transactions">
                      {{ formattedNumbers(group.students[0].total) }}
                    </commission-transactions-tooltip>
                  </td>
                </tr>
                <!-- Additional rows within the group (if any) -->
                <tr v-for="(student, studentIndex) in group.students.slice(1)" :key="`${groupIndex}-${student.student.id}-${studentIndex}`" :class="getTypeClass(student.type, student.status)">
                  <td class="text-center">{{ getStudentType(student.type, student.status) }}</td>
                  <td :title="student.course">{{ truncatedName(student.course) }}</td>
                  <td>{{ student.tuition_fee ? formattedNumbers(student.tuition_fee) : '-' }}</td>
                  <td>{{ formattedNumbers(student.commission) }}</td>
                  <td>{{ getQtyPercentage(student.qty_percentage) }}</td>
                  <td>{{ getEnhancerPercentage(student.enhancers, ['level', 'key_partner']) }}</td>
                  <td>{{ getEnhancerPercentage(student.enhancers, ['referer', 'hunter', 'self']) }}</td>
                  <td class="commission-subtotal text-right">
                    <commission-transactions-tooltip :transactions="student.transactions">
                      {{ formattedNumbers(student.total) }}
                    </commission-transactions-tooltip>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr>
                <th class="align-top pt-3 pb-0 pl-3" colspan="4">
                  {{ getTotalStudents() }}
                </th>
                <th class="align-top pt-3 pb-0">
                  {{ formattedNumbers(getTotal('tuition_fee')) }}
                </th>
                <th class="align-top pt-3 pb-0" colspan="1">
                  {{ formattedNumbers(getTotal('commission')) }}
                </th>
                <th class="align-top text-right pt-3 pb-0" colspan="4">
                  <p class="total-earnings title mb-0">
                    <span class="font-weight-normal">Total Earnings</span>
                    <span class="font-weight-bold">
                      {{ formattedNumbers(getTotal('total')) }}
                    </span>
                  </p>
                </th>
              </tr>
              <tr>
                <th colspan="10" class="text-right border-top-0 pt-0">
                  <p class="text-nowrap mb-0">
                    <span class="font-weight-normal">Total Paid</span>
                    {{ formattedNumbers(getTotal(isAccepted ? 'real_paid' : 'paid_total', ['paid', 'paid_def'])) }}
                  </p>
                  <p class="text-nowrap mb-0">
                    <span class="font-weight-normal">Pending</span>
                    {{ formattedNumbers(getPendingAmount()) }}
                  </p>
                </th>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="commissions-total text-center">
          <p class="subtotal-commissions mb-0">
            <span :class="{'total-earnings': hasCoeRows && isAccepted}" @click="hasCoeRows && isAccepted && $emit('viewEarned', groupedStudentsWithCoe)">
              Total Paid for {{ formattedPeriod(month, year, true) + ' ' + formattedNumbers(isAccepted ? totalCommissionsEarned : 0) }}
            </span>
            <span> +
              <span :class="{'total-prev-months': hasPreviousCommissions}" @click="hasPreviousCommissions && $emit('viewPrev', previousCommissions)">
                Total Earnings Previous Month {{ formattedNumbers(getTotalPrevMonths()) }}
              </span>
            </span>
          </p>
          <p class="total-commissions mb-0">
            Total = {{ formattedNumbers(getTotalCommissions()) }}
          </p>
          <small v-if="errorMessageForPrevCommissions">
            {{ errorMessageForPrevCommissions }}
          </small>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { closedStudents, previousMonths, totalEarned } from "../../../services/api/commissions";
import { formatCurrency, formatPeriod, truncateText } from "../../../utils/helpers";
import CommissionTransactionsTooltip from "./CommissionTransactionsTooltip.vue";

import '../../../../sass/commissions/closed-students-table.scss';

export default {
  name: 'ClosedStudentsTable',
  components: { CommissionTransactionsTooltip },
  props: {
    userId: {
      type: Number,
      default: 0,
    },
    isAccepted: {
      type: Boolean,
      default: false
    },
    month: {
      type: Number,
      default: 0
    },
    year: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isLoadingCommissions: false,
      isLoadingPreviousCommissions: false,
      isLoadingEarnedCommissions: false,
      hasPreviousCommissions: false,
      students: [],
      previousCommissions: [],
      earnedCommissions: [],
      errorMessage: null,
      errorMessageForPrevCommissions: null,
      totalCommissionsEarned: 0
    }
  },
  created() {
    this.fetchClosedStudents();
  },
  computed: {
    groupedStudents() {
      const groups = {};
      this.students.forEach((student, index) => {
        const studentId = student.student.id;
        if (!groups[studentId]) {
          groups[studentId] = {
            count: 0,
            students: [],
            originalIndex: index
          };
        }
        groups[studentId].count++;
        groups[studentId].students.push(student);
      });

      // Define the sorting order for type and status
      const typeStatusOrder = {
        'commission-paid': 1,
        'commission-paid_def': 2,
        'commission-pending': 3,
        'closed-to_check': 4,
        'closed-pending': 5,
        'prevision-pending': 6,
        'commission-cancelled': 7,
        'closed-cancelled': 8,
      };

      // Sort each group of students based on the defined sequence
      Object.values(groups).forEach(group => {
        group.students.sort((firstStudent, secondStudent) => {
          const firstStudentKey = `${firstStudent.type}-${firstStudent.status}`;
          const secondStudentKey = `${secondStudent.type}-${secondStudent.status}`;
          const orderComparison = typeStatusOrder[firstStudentKey] - typeStatusOrder[secondStudentKey];

          // Maintain their original sequence when they have the same order
          if (orderComparison !== 0) {
            return orderComparison;
          }

          return this.students.indexOf(firstStudent) - this.students.indexOf(secondStudent);
        });
      });

      // Sort groups based on the type and status order and original index
      return Object.values(groups).sort((firstGroup, secondGroup) => {
        const firstGroupKey = `${firstGroup.students[0].type}-${firstGroup.students[0].status}`;
        const secondGroupKey = `${secondGroup.students[0].type}-${secondGroup.students[0].status}`;
        const typeStatusComparison = typeStatusOrder[firstGroupKey] - typeStatusOrder[secondGroupKey];

        // Maintain their original sequence when they have the same order
        if (typeStatusComparison !== 0) {
          return typeStatusComparison;
        }

        // If the same type and status, sort by the original index
        return firstGroup.originalIndex - secondGroup.originalIndex;
      });
    },
    groupedCommissions() {
      const groups = {};
      this.earnedCommissions.forEach((student, index) => {
        const studentId = student.student.id;
        if (!groups[studentId]) {
          groups[studentId] = {
            count: 0,
            students: [],
            originalIndex: index
          };
        }
        groups[studentId].count++;
        groups[studentId].students.push(student);
      });

      // Sort each group of students by their original sequence
      Object.values(groups).forEach(group => {
        group.students.sort((a, b) => {
          const aIndex = this.earnedCommissions.indexOf(a);
          const bIndex = this.earnedCommissions.indexOf(b);
          return aIndex - bIndex;
        });
      });

      // Sort groups based on the type order: commission, closed, prevision
      return Object.values(groups).sort((a, b) => {
        const typeOrder = { commission: 1, closed: 2, prevision: 3 };
        const aType = a.students[0].type;
        const bType = b.students[0].type;

        // Sort by type first
        const typeComparison = typeOrder[aType] - typeOrder[bType];
        if (typeComparison !== 0) return typeComparison;

        return a.originalIndex - b.originalIndex;
      });
    },
    groupedStudentsWithCoe() {
      return this.groupedCommissions.filter(group =>
        group.students.some(student => this.getTypeClass(student.type, student.status).includes('has-coe'))
      );
    },
    hasCoeRows() {
      return this.students.some(student => this.getTypeClass(student.type, student.status).includes('has-coe'));
    },
  },
  watch: {
    userId: 'fetchClosedStudents',
    isAccepted: 'fetchClosedStudents',
    month: 'fetchClosedStudents',
    year: 'fetchClosedStudents'
  },
  methods: {
    formattedPeriod(month, year, mode = false, uppercase = false) {
      return formatPeriod(month, year, mode, uppercase);
    },
    formattedNumbers(number) {
      return formatCurrency(number);
    },
    truncatedName(name, limit = 20) {
      return truncateText(name, limit);
    },
    async fetchClosedStudents() {
      try {
        this.isLoadingCommissions = true;

        const response = await closedStudents(this.userId, this.year, this.month);

        if (response.status === 200) {
          this.students = response.data;
          this.errorMessage = null;

          await this.fetchPreviousCommissions();
          await this.fetchEarnedCommissions();
        }
      } catch (error) {
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        } else {
          this.errorMessage = 'An unexpected error occurred';
        }
      } finally {
        this.isLoadingCommissions = false;
      }
    },
    async fetchPreviousCommissions() {
      try {
        this.isLoadingPreviousCommissions = true;
        const response = await previousMonths(this.userId, this.year, this.month);

        if (response.status === 200) {
          this.previousCommissions = response.data;
          this.hasPreviousCommissions = this.previousCommissions.length > 0;
          this.errorMessageForPrevCommissions = null;
        }
      } catch (error) {
        if (error.response?.data?.error) {
          this.errorMessageForPrevCommissions = error.response.data.error;
        } else {
          this.errorMessageForPrevCommissions = 'An unexpected error occurred';
        }
      } finally {
        this.isLoadingPreviousCommissions = false;
      }
    },
    async fetchEarnedCommissions() {
      try {
        this.isLoadingEarnedCommissions = true;
        const response = await totalEarned(this.userId, this.year, this.month);

        if (response.status === 200) {
          this.earnedCommissions = response.data;

          this.totalCommissionsEarned = this.earnedCommissions.reduce((total, commission) => {
            return total + parseFloat(commission.total);
          }, 0);
        }
      } catch (error) {
        console.error('Error in view earned commissions:', error);
      } finally {
        this.isLoadingEarnedCommissions = false;
      }
    },
    getQtyPercentage(student_qty) {
      return student_qty ? `${student_qty}%` : '-';
    },
    getEnhancerPercentageLevel(enhancers) {
      console.log(enhancers);
      const levelEnhancer = enhancers.find(enh => enh.type === 'level');
      console.log(levelEnhancer)
      if (levelEnhancer) {
        return `${levelEnhancer.percentage}%`;
      }
      const keyPartnerEnhancer = enhancers.find(enh => enh.type === 'key_partner');
      return keyPartnerEnhancer ? `${keyPartnerEnhancer.percentage}%` : '-';
    },
    getEnhancerPercentage(enhancers, types) {
      const enhancer = enhancers.find(enh => types.includes(enh.type));
      if (enhancer) {
        if(enhancer.type === 'level') {
          return `${enhancer.percentage}% (${enhancer.level})`;
        }else {
          return `${enhancer.percentage}% (${enhancer.type})`
        }
      }else {
        return '-';
      }
      //return enhancer ? `${enhancer.percentage}%` : '-';
    },
    getTotal(metric, statuses = []) {
      return this.students.reduce((sum, student) => {
        if (statuses.length === 0 || statuses.includes(student.status)) {
          return sum + (parseFloat(student[metric]) || 0);
        }
        return sum;
      }, 0).toFixed(2);
    },
    getTotalPrevMonths() {
      return this.previousCommissions.flatMap(period => [...period.data, ...period.transactions])
        .reduce((total, item) => total + parseFloat(item.total), 0)
        .toFixed(2);
    },
    getTotalCommissions() {
      const totalCurrent = parseFloat(this.totalCommissionsEarned) || 0;
      const totalPending = parseFloat(this.getTotalPrevMonths()) || 0;

      return totalCurrent + totalPending;
    },
    getPendingAmount() {
      const total = parseFloat(this.getTotal('total')) || 0;
      const paidTotal = parseFloat(this.getTotal(this.isAccepted ? 'real_paid' : 'paid_total', ['paid', 'paid_def'])) || 0;
      return total - paidTotal;
    },
    getTotalStudents() {
      const uniqueStudentIds = new Set(this.students.map(student => student.student.id));
      const count = uniqueStudentIds.size;

      if (count === 0) {
        return 'No Students found';
      } else {
        return count + (count > 1 ? ' Students' : ' Student');
      }
    },
    getStudentType(type, status) {
      switch (type) {
        case 'commission':
          if (status === 'cancelled') {
            return 'Cancelled'
          }

          return 'COE/LOA';
        case 'closed':
          if (status === 'cancelled') {
            return 'Cancelled'
          }else if(status === 'to_check'){
            return 'To Check'
          }

          return 'No COE/LOA';
        default:
          return 'Prevision';
      }
    },
    getTypeClass(type, status) {
      switch (type) {
        case 'commission':
          if (status === 'paid' || status === 'paid_def') {
            return 'has-coe'
          } else if (status === 'cancelled') {
            return 'cancelled'
          }

          return 'has-coe-pending';
        case 'closed':
          if (status === 'cancelled') {
            return 'cancelled'
          }else if(status === 'to_check'){
            return 'has-check'
          }
          return 'has-no-coe';
        default:
          return '';
      }
    }
  }
}
</script>